/**
 *  @class
 *  @function BundleSelector
 *  
 *  Handles bundle card selection and multi-product add to cart
 */
if (!customElements.get('bundle-selector')) {
  class BundleSelector extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.cards = this.querySelectorAll('.bundle-card');
      this.radios = this.querySelectorAll('.bundle-card__radio');
    }

    connectedCallback() {
      this.setupEventListeners();
      this.interceptAddToCart();
    }

    setupEventListeners() {
      // Handle card clicks
      this.cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking on the radio directly (it handles itself)
          if (e.target.classList.contains('bundle-card__radio')) return;
          
          const radio = card.querySelector('.bundle-card__radio');
          if (radio) {
            radio.checked = true;
            this.updateSelectedCard();
          }
        });
      });

      // Handle radio changes
      this.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.updateSelectedCard();
        });
      });
    }

    updateSelectedCard() {
      this.cards.forEach((card) => {
        const radio = card.querySelector('.bundle-card__radio');
        card.classList.toggle('bundle-card--selected', radio && radio.checked);
      });

      // Dispatch event for other components to listen to
      const selectedCard = this.querySelector('.bundle-card--selected');
      if (selectedCard) {
        dispatchCustomEvent('bundle:selection-changed', {
          sectionId: this.sectionId,
          variantIds: selectedCard.dataset.variantIds,
          productCount: selectedCard.dataset.productCount,
          bundleIndex: selectedCard.dataset.bundleIndex
        });
      }
    }

    getSelectedBundle() {
      const selectedCard = this.querySelector('.bundle-card--selected');
      if (!selectedCard) return null;

      const variantIds = selectedCard.dataset.variantIds;
      if (!variantIds) return null;

      return {
        variantIds: variantIds.split(',').filter(id => id),
        productCount: parseInt(selectedCard.dataset.productCount) || 1,
        bundleIndex: selectedCard.dataset.bundleIndex
      };
    }

    interceptAddToCart() {
      // Find the product form in the same section
      const productForm = document.querySelector(`#product-form-${this.sectionId}`);
      
      if (productForm) {
        // Override the submit behavior - use capture phase to run before other handlers
        productForm.addEventListener('submit', (e) => {
          const bundle = this.getSelectedBundle();
          if (bundle && bundle.variantIds.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation(); // Prevent other handlers from running
            this.handleBundleAddToCart(productForm);
          }
        }, true);
      }

      // Also intercept the add to cart button directly for extra safety
      const addToCartButton = document.querySelector(`#product-form-${this.sectionId}`)?.querySelector('[type="submit"]');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', (e) => {
          const bundle = this.getSelectedBundle();
          if (bundle && bundle.variantIds.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation(); // Prevent other handlers from running
            this.handleBundleAddToCart();
          }
        }, true);
      }
    }

    async handleBundleAddToCart(form) {
      const bundle = this.getSelectedBundle();
      
      if (!bundle || bundle.variantIds.length === 0) {
        // No bundle selected, fall back to regular behavior
        if (form) {
          form.submit();
        }
        return;
      }

      // Show loading state
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      submitButtons.forEach((btn) => {
        btn.classList.add('loading');
        btn.setAttribute('aria-disabled', 'true');
      });

      try {
        // Build items array for cart add
        const items = bundle.variantIds.map(id => ({
          id: parseInt(id),
          quantity: 1
        }));

        // Add all items to cart
        const response = await fetch(`${window.theme?.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (result.status) {
          // Error occurred
          this.handleError(result.description || result.message);
          return;
        }

        // Success - now fetch updated cart and open drawer
        await this.refreshCartAndOpenDrawer();

        // Dispatch success event
        dispatchCustomEvent('cart:items-added', {
          items: result.items || [result],
          bundle: bundle
        });

      } catch (error) {
        console.error('Bundle add to cart error:', error);
        this.handleError('Could not add items to cart. Please try again.');
      } finally {
        // Remove loading state
        submitButtons.forEach((btn) => {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
        });
      }
    }

    async refreshCartAndOpenDrawer() {
      try {
        // Fetch updated cart sections
        const sectionsToRender = [
          { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
        ];

        const sectionNames = sectionsToRender.map(s => s.section).join(',');
        const response = await fetch(`${window.location.pathname}?sections=${sectionNames}`);
        const sections = await response.json();

        // Update cart sections
        sectionsToRender.forEach((section) => {
          const element = document.getElementById(section.id);
          if (element && sections[section.section]) {
            const html = new DOMParser().parseFromString(sections[section.section], 'text/html');
            const newContent = html.querySelector(section.selector);
            const targetElement = element.querySelector(section.selector) || element;
            
            if (newContent && targetElement) {
              targetElement.innerHTML = newContent.innerHTML;
            }
          }
        });

        // Reinitialize cart drawer if needed
        if (typeof CartDrawer !== 'undefined') {
          new CartDrawer();
        }

        // Open cart drawer
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (cartDrawer) {
          document.body.classList.add('open-cc');
          document.body.classList.add('open-cart');
          cartDrawer.classList.add('active');
          dispatchCustomEvent('cart-drawer:open');
        }

      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    handleError(message) {
      // Try to show error in product form
      const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
      const errorMessage = document.querySelector('.product-form__error-message');
      
      if (errorWrapper && errorMessage) {
        errorWrapper.removeAttribute('hidden');
        errorMessage.textContent = message;
      } else {
        // Fallback to alert
        alert(message);
      }

      dispatchCustomEvent('bundle:add-error', { message });
    }
  }

  customElements.define('bundle-selector', BundleSelector);
}

// Helper function if not already defined
if (typeof dispatchCustomEvent !== 'function') {
  function dispatchCustomEvent(eventName, data) {
    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: true
    });
    document.dispatchEvent(event);
  }
}